import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import StaffNavbar from '../components/StaffNavbar';
import BackButton from '../components/BackButton';
import BillingStatusIcon from '../components/BillingStatusIcon';
import apiService from '../services/api';
import { buildSentIndex, groupSchoolPayByMonth } from '../utils/schoolPay';
import { FUNNEL_STATE } from '../utils/funnel';
import '../styles/StaffPages.css';

/**
 * What we owe each school, by month — the mirror of Merchant Invoices.
 *
 * Both pages recompute from the same transaction feed, so the commission a
 * merchant owes and the share a school is due can never disagree about a month.
 * The only stored state here is whether a statement has been sent, which is the
 * one thing that cannot be derived: without it there is no way to tell a month
 * that has been paid from one nobody has looked at yet.
 */

const formatMoney = (n) => `$${Number(n || 0).toFixed(2)}`;

/**
 * The bare check the rest of the app draws, not a check in a ring: `sent` here
 * is the same fact `distributed` means on the funnel, so it is taken from
 * FUNNEL_STATE rather than redrawn with a copied colour.
 */
const SentCheck = ({ sent }) =>
  sent ? (
    <BillingStatusIcon state={{ ...FUNNEL_STATE.distributed, label: 'Sent' }} size={18} />
  ) : (
    // Nothing is drawn for an unsent month on purpose: an icon would read as a
    // problem, when in fact it just has not been sent yet.
    <span aria-label="Not sent" />
  );

const SuperuserSchoolPay = () => {
  const [transactions, setTransactions] = useState([]);
  const [schools, setSchools] = useState([]);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      try {
        const currentUser = await apiService.fetchCurrentUser();
        if (!currentUser?.is_superuser) {
          navigate('/staff/dashboard/');
          return;
        }
        const [txnRes, schoolRes, overviewRes] = await Promise.all([
          apiService.getAllTransactionsSuperuser(),
          apiService.getSchools(),
          // Sent state is a nice-to-have; a failure here must not stop the
          // figures rendering.
          apiService.getSchoolPayOverview().catch(() => null),
        ]);
        setTransactions(txnRes?.transactions || []);
        const list = Array.isArray(schoolRes) ? schoolRes : schoolRes?.results || [];
        setSchools(list);
        setOverview(overviewRes);
      } catch (err) {
        console.error('Failed to load school pay data:', err);
        setError('Failed to load transactions.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [navigate]);

  const schoolsById = useMemo(() => {
    const map = {};
    for (const s of schools) map[String(s.id)] = s;
    return map;
  }, [schools]);

  const grouped = useMemo(
    () => groupSchoolPayByMonth(transactions, schoolsById),
    [transactions, schoolsById]
  );

  const sentIndex = useMemo(() => buildSentIndex(overview), [overview]);

  if (loading) {
    return (
      <div className="admin-dashboard">
        <StaffNavbar activeSection="superuser" />
        <div className="admin-main">
          <div className="admin-content">
            <BackButton text="Back to Dashboard" to="/superuser/dashboard/" />
            <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>Loading...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <StaffNavbar activeSection="superuser" />
      <div className="admin-main">
        <div className="admin-content">
          <style>{`
            .schoolpay-row-grid {
              display: grid;
              grid-template-columns: minmax(0, 2fr) 110px 130px 150px 40px;
              gap: 12px;
              align-items: center;
            }
            @media (max-width: 720px) {
              .schoolpay-row-grid { grid-template-columns: minmax(0, 1fr) 120px 40px; }
              .schoolpay-col-hide-sm { display: none; }
            }
          `}</style>
          <BackButton text="Back to Dashboard" to="/superuser/dashboard/" />
          <h1 className="staff-page-header">School Pay</h1>
          <p className="staff-page-subheader">Monthly payment statements by school</p>

          {error && <div style={{ marginTop: '24px', color: '#b00020' }}>{error}</div>}

          {!error && grouped.length === 0 && (
            <div style={{ marginTop: '32px', color: '#666' }}>No transactions found.</div>
          )}

          {grouped.map((month) => (
            <div key={month.key} style={{ marginTop: '48px' }}>
              <h2
                className="staff-section-header"
                style={{ fontSize: '22px', fontWeight: 700, marginBottom: '8px' }}
              >
                {month.label}
              </h2>
              <p className="staff-page-content" style={{ marginBottom: '16px' }}>
                {month.schools.length} school{month.schools.length === 1 ? '' : 's'} with earnings
                {' · '}
                {month.schools.filter((s) => sentIndex.lookup(s.schoolId, month.key)?.is_sent).length} sent
              </p>

              <div style={{
                border: '1px solid #e9ecef',
                borderRadius: '12px',
                overflow: 'hidden',
                background: 'white',
              }}>
                <div
                  className="schoolpay-row-grid"
                  style={{
                    padding: '14px 20px',
                    background: '#f8f9fa',
                    fontSize: '12px',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    color: '#666',
                  }}
                >
                  <div>School</div>
                  <div className="schoolpay-col-hide-sm" style={{ textAlign: 'right' }}>Transactions</div>
                  <div className="schoolpay-col-hide-sm" style={{ textAlign: 'right' }}>Sales</div>
                  <div style={{ textAlign: 'right' }}>School share</div>
                  <div aria-hidden="true" />
                </div>
                {month.schools.map((s) => {
                  const statement = sentIndex.lookup(s.schoolId, month.key);
                  return (
                    <button
                      key={`${month.key}-${s.schoolId}`}
                      type="button"
                      onClick={() => navigate(`/superuser/school-pay/${month.key}/${s.schoolId}/`)}
                      className="schoolpay-row-grid"
                      style={{
                        width: '100%',
                        padding: '16px 20px',
                        border: 'none',
                        borderTop: '1px solid #f0f0f0',
                        background: 'white',
                        cursor: 'pointer',
                        textAlign: 'left',
                        fontFamily: 'inherit',
                        fontSize: '15px',
                        color: '#1a1a1a',
                        transition: 'background 0.15s ease',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = '#f8fafb'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'white'; }}
                    >
                      <div style={{ fontWeight: 500, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {s.name}
                      </div>
                      <div className="schoolpay-col-hide-sm" style={{ textAlign: 'right', color: '#666' }}>{s.txnCount}</div>
                      <div className="schoolpay-col-hide-sm" style={{ textAlign: 'right', color: '#666' }}>{formatMoney(s.totalSales)}</div>
                      <div style={{ textAlign: 'right', fontWeight: 600 }}>{formatMoney(s.totalShare)}</div>
                      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                        <SentCheck sent={Boolean(statement?.is_sent)} />
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SuperuserSchoolPay;
